import React, {useContext} from 'react';

import {NumberInput} from 'src/components/input';
import {WidthItem} from 'src/components/width-item';
import {MessageContext} from 'src/contexts/message-context';
import {mapDateFormat} from 'src/utils/mapDateFormat';

import {Dropdown} from '@pb/kit-component-dropdown';
import {FormattedDate, Integer} from '@pb/kit-component-formatters';
import {Group} from '@pb/kit-component-group';
import {Icon, Icons} from '@pb/kit-component-icon';
import {BasicIconButton} from '@pb/kit-component-icon-button';
import {LocaleContext} from '@pb/kit-context-locale';

import {AI_TOKEN_LABELS, isAiTokenAmountValid} from '../utils/ai-token-limits';

import type {AiTokenLimitPeriod} from 'src/openapi/admin/models/AiTokenLimitPeriod';

type Props = {
    period: `${AiTokenLimitPeriod}`,
    defaultValue: number,
    customValue?: number,
    temporaryValue?: number,
    usedValue?: number,
    until?: string,
    disabled?: boolean,
    onChange: (amount: number) => void,
    onClearCustom?: () => void,
    onClearTemporary?: () => void,
};

export const AiTokenLimitItem = ({
    period, defaultValue, customValue, temporaryValue, usedValue, until,
    disabled, onChange, onClearCustom, onClearTemporary,
}: Props) => {
    const {locale} = useContext(LocaleContext);
    const {setErrorMessage} = useContext(MessageContext);
    const value = temporaryValue ?? customValue ?? defaultValue;
    const format = (amount: number) => Integer.format(amount, locale);
    const label = AI_TOKEN_LABELS[period];
    const hasOverride = customValue !== undefined || temporaryValue !== undefined;
    const displayedValue = format(value) + (usedValue ? ` (${format(usedValue)} used)` : '');

    const handleBlur = (amount: number | undefined) => {
        if (disabled) {
            return;
        }
        if (!isAiTokenAmountValid(amount)) {
            setErrorMessage('Enter a non-negative whole number for the AI token limit');
            return;
        }
        if (amount !== value) {
            onChange(amount);
        }
    };

    return (
        <NumberInput
            id={`ai-token-limit-${period}`}
            labelId={`ai-token-limit-${period}-label`}
            width="100%"
            value={value}
            displayedValue={displayedValue}
            minValue={0}
            maxValue={Number.MAX_SAFE_INTEGER}
            readOnly={disabled}
            onBlur={handleBlur}
            label={hasOverride ? (
                <WidthItem width="100%">
                    <Group justify="space-between">
                        <span>{label}</span>
                        <Dropdown
                            hoverEnabled={true}
                            anchor={({anchorRef, getAnchorProps}) => (
                                <span ref={anchorRef} {...getAnchorProps()}>
                                    <Group gap="xxs" alignItems="center">
                                        {customValue !== undefined && <Icon type={Icons.COGS}/>}
                                        {temporaryValue !== undefined && until ? (
                                            <>
                                                <Icon type={Icons.CALENDAR}/>
                                                <b>until {FormattedDate.format({
                                                    value: new Date(`${until}T12:00:00`),
                                                    format: mapDateFormat(locale.dateFormat, 'WORD'),
                                                })}
                                                </b>
                                            </>
                                        ) : <b>custom</b>}
                                    </Group>
                                </span>
                            )}>
                            <Group direction="v" gap="xs">
                                {temporaryValue !== undefined && (
                                    <Group gap="xs" alignItems="center">
                                        <Icon type={Icons.CALENDAR}/>
                                        <span>Temporary:</span><b>{format(temporaryValue)}</b>
                                        {!disabled && onClearTemporary ? (
                                            <BasicIconButton
                                                title="Clear Temporary Limit"
                                                iconProps={{type: Icons.CLOSE}}
                                                onClick={onClearTemporary}
                                            />
                                        ) : null}
                                    </Group>
                                )}
                                {customValue !== undefined && (
                                    <Group gap="xs" alignItems="center">
                                        <Icon type={Icons.COGS}/>
                                        <span>Custom:</span><b>{format(customValue)}</b>
                                        {!disabled && onClearCustom ? (
                                            <BasicIconButton
                                                title="Clear Custom Limit"
                                                iconProps={{type: Icons.CLOSE}}
                                                onClick={onClearCustom}
                                            />
                                        ) : null}
                                    </Group>
                                )}
                                <Group gap="xs"><span>Default:</span><b>{format(defaultValue)}</b></Group>
                            </Group>
                        </Dropdown>
                    </Group>
                </WidthItem>
            ) : label}
        />
    );
};
